import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  Check,
  CheckCircle2,
  Clock,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  getAgendaStatus,
  getDaysUntil,
  useAgendaStore,
  type AgendaItem,
  type AgendaStatus,
} from '@/stores/agenda';
import { useFleetStore } from '@/stores/fleet';
import { cn, formatDateTime } from '@/lib/utils';

const STATUS_META: Record<
  AgendaStatus,
  { label: string; color: string; bg: string; icon: typeof CalendarClock; order: number }
> = {
  overdue: {
    label: 'Vencidas',
    color: 'text-status-blocked',
    bg: 'bg-status-blocked/10',
    icon: AlertTriangle,
    order: 0,
  },
  soon: {
    label: 'Próximas',
    color: 'text-status-ready',
    bg: 'bg-status-ready/10',
    icon: Clock,
    order: 1,
  },
  future: {
    label: 'Futuras',
    color: 'text-primary',
    bg: 'bg-primary/10',
    icon: CalendarClock,
    order: 2,
  },
  completed: {
    label: 'Concluídas',
    color: 'text-status-off',
    bg: 'bg-status-off/15',
    icon: CheckCircle2,
    order: 3,
  },
};

export function AgendaPage() {
  const items = useAgendaStore((s) => s.items);
  const [editing, setEditing] = useState<AgendaItem | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = items.filter((it) => {
      if (!showCompleted && getAgendaStatus(it) === 'completed') return false;
      if (q) {
        const hay = `${it.title} ${it.description ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return list.sort((a, b) => {
      const sa = getAgendaStatus(a);
      const sb = getAgendaStatus(b);
      if (sa !== sb) return STATUS_META[sa].order - STATUS_META[sb].order;
      return a.dueDate.localeCompare(b.dueDate);
    });
  }, [items, search, showCompleted]);

  const grouped = useMemo(() => {
    const out: Record<AgendaStatus, AgendaItem[]> = {
      overdue: [],
      soon: [],
      future: [],
      completed: [],
    };
    for (const it of filtered) out[getAgendaStatus(it)].push(it);
    return out;
  }, [filtered]);

  const counts = useMemo(() => {
    const c: Record<AgendaStatus, number> = {
      overdue: 0,
      soon: 0,
      future: 0,
      completed: 0,
    };
    for (const it of items) c[getAgendaStatus(it)] += 1;
    return c;
  }, [items]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Agenda de manutenções</h1>
          <p className="text-sm text-muted-foreground">
            Lembretes locais por data. Funciona offline, salvo neste navegador.
          </p>
        </div>
        <Button onClick={() => setEditing('new')} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Novo lembrete
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <CountTile status="overdue" value={counts.overdue} />
        <CountTile status="soon" value={counts.soon} />
        <CountTile status="future" value={counts.future} />
        <CountTile status="completed" value={counts.completed} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar"
            className="rounded-full bg-secondary pl-9"
          />
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            className="h-4 w-4 cursor-pointer rounded border-border accent-primary"
          />
          Mostrar concluídas
        </label>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <CalendarPlus className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {items.length === 0
                ? 'Sem lembretes ainda. Crie o primeiro clicando em Novo lembrete.'
                : 'Nada corresponde aos filtros.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([status, list]) =>
          list.length === 0 ? null : (
            <section key={status} className="space-y-2">
              <h2 className="flex items-center gap-2 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {STATUS_META[status as AgendaStatus].label}
                <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] text-foreground">
                  {list.length}
                </span>
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((item) => (
                  <AgendaCard key={item.id} item={item} onEdit={() => setEditing(item)} />
                ))}
              </div>
            </section>
          ),
        )
      )}

      {editing && (
        <AgendaEditor
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function CountTile({ status, value }: { status: AgendaStatus; value: number }) {
  const meta = STATUS_META[status];
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-1 p-3 text-center">
        <span className={cn('flex h-9 w-9 items-center justify-center rounded-full', meta.bg, meta.color)}>
          <meta.icon className="h-4 w-4" />
        </span>
        <p className="text-xl font-bold leading-none">{value}</p>
        <p className="text-[10px] font-medium uppercase text-muted-foreground">{meta.label}</p>
      </CardContent>
    </Card>
  );
}

function AgendaCard({ item, onEdit }: { item: AgendaItem; onEdit: () => void }) {
  const devices = useFleetStore((s) => s.devices);
  const device = item.deviceId != null ? devices.get(item.deviceId) : undefined;
  const status = getAgendaStatus(item);
  const meta = STATUS_META[status];
  const daysUntil = getDaysUntil(item.dueDate);

  const markCompleted = useAgendaStore((s) => s.markCompleted);
  const snooze = useAgendaStore((s) => s.snooze);
  const remove = useAgendaStore((s) => s.remove);

  let timeLabel = '';
  if (status === 'overdue') timeLabel = `Vencida há ${-daysUntil}d`;
  else if (status === 'soon')
    timeLabel = daysUntil === 0 ? 'Vence hoje' : `Vence em ${daysUntil}d`;
  else if (status === 'future') timeLabel = `Em ${daysUntil}d`;
  else timeLabel = 'Concluída';

  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', meta.bg, meta.color)}>
              <meta.icon className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{item.title}</p>
              {item.description && (
                <p className="truncate text-xs text-muted-foreground">{item.description}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
            aria-label="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        {device && (
          <p className="flex items-center gap-1.5 text-xs">
            <Truck className="h-3.5 w-3.5 text-primary" />
            <span className="truncate font-semibold">{device.name}</span>
          </p>
        )}

        <div className="flex items-center gap-2 text-xs">
          <span className={cn('font-bold', meta.color)}>{timeLabel}</span>
          <span className="text-muted-foreground">·</span>
          <span className="font-mono text-muted-foreground">
            {new Date(`${item.dueDate}T00:00:00`).toLocaleDateString('pt-BR')}
          </span>
          {item.recurrenceDays && item.recurrenceDays > 0 && (
            <span className="rounded bg-secondary px-1.5 text-[10px] font-semibold text-muted-foreground">
              a cada {item.recurrenceDays}d
            </span>
          )}
        </div>

        {status !== 'completed' && (
          <div className="flex flex-wrap gap-1 pt-1">
            <Button size="sm" variant="outline" onClick={() => markCompleted(item.id)} className="gap-1">
              <Check className="h-3 w-3" />
              Concluir
            </Button>
            <Button size="sm" variant="ghost" onClick={() => snooze(item.id, 1)} className="gap-1">
              <RotateCcw className="h-3 w-3" />
              +1d
            </Button>
            <Button size="sm" variant="ghost" onClick={() => snooze(item.id, 7)} className="gap-1">
              <RotateCcw className="h-3 w-3" />
              +7d
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (window.confirm(`Excluir "${item.title}"?`)) remove(item.id);
              }}
              className="ml-auto gap-1 text-status-blocked hover:bg-status-blocked/10 hover:text-status-blocked"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}

        {item.completedDates.length > 0 && (
          <p className="text-[10px] text-muted-foreground">
            Última conclusão: {new Date(`${item.completedDates[0]}T00:00:00`).toLocaleDateString('pt-BR')}
            {item.completedDates.length > 1 && ` (+${item.completedDates.length - 1} antes)`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface FormState {
  title: string;
  description: string;
  deviceId: number | null;
  dueDate: string;
  recurrenceDays: string;
}

function fromItem(item: AgendaItem): FormState {
  return {
    title: item.title,
    description: item.description ?? '',
    deviceId: item.deviceId ?? null,
    dueDate: item.dueDate,
    recurrenceDays: item.recurrenceDays ? String(item.recurrenceDays) : '',
  };
}

const TODAY_ISO = new Date().toISOString().slice(0, 10);

function AgendaEditor({ initial, onClose }: { initial: AgendaItem | null; onClose: () => void }) {
  const add = useAgendaStore((s) => s.add);
  const update = useAgendaStore((s) => s.update);
  const remove = useAgendaStore((s) => s.remove);

  const devices = useFleetStore((s) => s.devices);
  const sortedDevices = useMemo(
    () => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [devices],
  );

  const [form, setForm] = useState<FormState>(
    initial
      ? fromItem(initial)
      : {
          title: '',
          description: '',
          deviceId: null,
          dueDate: TODAY_ISO,
          recurrenceDays: '',
        },
  );
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (initial) setForm(fromItem(initial));
  }, [initial]);

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.title.trim()) e.title = 'Obrigatório';
    if (!form.dueDate) e.dueDate = 'Obrigatório';
    return e;
  }, [form]);

  const valid = Object.keys(errors).length === 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    const r = Number(form.recurrenceDays);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      deviceId: form.deviceId ?? undefined,
      dueDate: form.dueDate,
      recurrenceDays: Number.isFinite(r) && r > 0 ? r : undefined,
    };
    if (initial) update(initial.id, payload);
    else add(payload);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[95vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">
            {initial ? 'Editar lembrete' : 'Novo lembrete'}
          </h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <Field label="Título" required error={touched ? errors.title : undefined}>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ex: Troca de óleo, Limpeza filtro, Calibração"
              autoFocus
            />
          </Field>
          <Field label="Descrição (opcional)">
            <Input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Field>
          <Field label="Máquina (opcional)">
            <select
              value={form.deviceId ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, deviceId: e.target.value ? Number(e.target.value) : null }))}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Sem máquina específica</option>
              {sortedDevices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.model ? ` · ${d.model}` : ''}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data" required error={touched ? errors.dueDate : undefined}>
              <Input
                type="date"
                value={form.dueDate}
                onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
              />
            </Field>
            <Field label="Recorrência (dias)">
              <Input
                type="number"
                min={0}
                value={form.recurrenceDays}
                onChange={(e) => setForm((f) => ({ ...f, recurrenceDays: e.target.value }))}
                placeholder="Opcional"
              />
            </Field>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Quando você marcar como concluído, se houver recorrência o lembrete avança automaticamente
            para a próxima data.
          </p>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (!window.confirm(`Excluir "${initial.title}"?`)) return;
                remove(initial.id);
                onClose();
              }}
              className="mr-auto gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              Excluir
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!valid} className="gap-1.5">
            <CalendarCheck className="h-4 w-4" />
            {initial ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}

void formatDateTime;
